<script>
import u from '@/utils'
import { mapState, mapGetters } from 'vuex'
import ProfilePicker from '@/components/profile/ProfilePicker'

export default {
  props: {
    value: {
      type: Array,
      default() {
        return []
      }
    },
    outerUpdating: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      editActive: false,
      selectedIds: [],
      innerProfileUpdating: false
    }
  },

  computed: {
    ...mapState({
      allProfiles: state => {
        return state.profiles.profiles
      }
    }),
    ...mapGetters({
      can: 'can'
    }),
    profileUpdating() {
      return this.innerProfileUpdating || this.outerUpdating
    }
  },

  watch: {
    value() {
      this.selectedIds = u.clone(this.value).map(selectedProfile => {
        return selectedProfile.hexId
      })
    }
  },

  methods: {
    onFinish() {
      this.editActive = false
      // get selected items from the tree view and set as this.selectedNodes
      this.$emit('finish', this.selectedIds)
    },

    onClose() {
      this.editActive = false
      this.$emit('close', this.selectedIds)
    },

    findInTree(items, id) {
      if (!items.length) {
        return new Error(
          `node cannot be found in tree..! id: ${id} items: ${items}`
        )
      }
      let item = items.find(item => item.id === id)
      if (item) {
        return item
      } else {
        let children = items
          .filter(item => item.children && item.children.length)
          .map(item => item.children)
          .flat()
        return this.findInTree(children, id)
      }
    }
  },

  async created() {
    this.innerProfileUpdating = true
    await this.$store.dispatch('fetchProfiles')
    this.innerProfileUpdating = false
  },

  components: {
    ProfilePicker
  }
}
</script>

<template>
  <v-card>
    <v-toolbar dense card class="elevation-2">
      <v-toolbar-title class="body-2 beez-bold ">Profiles</v-toolbar-title>
      <v-spacer></v-spacer>
      <transition name="fade" mode="out-in">
        <v-tooltip v-if="editActive" key="finish" bottom>
          <v-btn flat icon @click="onClose()" slot="activator">
            <v-icon>close</v-icon>
          </v-btn>
          <span>Close</span>
        </v-tooltip>
      </transition>
      <transition name="fade" mode="out-in">
        <v-tooltip v-if="editActive" key="finish" bottom>
          <v-btn flat icon @click="onFinish()" slot="activator">
            <v-icon>done</v-icon>
          </v-btn>
          <span>Update</span>
        </v-tooltip>
        <v-tooltip v-else key="edit" bottom>
          <v-btn
            flat
            icon
            @click="editActive = true"
            slot="activator"
            v-if="
              can(
                'AUTOMATION_SERVICE_PROFILE' ||
                  'WORKFLOW_PROFILES' ||
                  'BOT_PROFILES' ||
                  'RULE_PROFILE'
              )
            "
          >
            <v-icon>edit</v-icon>
          </v-btn>
          <span>Edit</span>
        </v-tooltip>
      </transition>
    </v-toolbar>
    <v-progress-linear
      indeterminate
      class="ma-0"
      color="accent"
      height="5"
      :active="profileUpdating"
    ></v-progress-linear>
    <transition name="bounce" mode="out-in">
      <v-list
        two-line
        v-if="!editActive"
        key="view-list"
        style="max-height: 760px"
        class="scroll-y"
      >
        <v-list-tile avatar v-if="!value.length">
          <v-list-tile-avatar>
            <v-btn icon ripple class="ma-1">
              <v-icon>error_outline</v-icon>
            </v-btn>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              <slot name="empty-list-placeholder-selected"
                >Please click the edit icon to start adding Profiles
              </slot>
            </v-list-tile-title>
            <v-list-tile-sub-title></v-list-tile-sub-title>
          </v-list-tile-content>
          <v-list-tile-action>
            <v-btn
              icon
              ripple
              @click="editActive = true"
              v-if="
                can(
                  'AUTOMATION_SERVICE_PROFILE' ||
                    'WORKFLOW_PROFILES' ||
                    'BOT_PROFILES' ||
                    'RULE_PROFILE'
                )
              "
            >
              <v-icon>add</v-icon>
            </v-btn>
          </v-list-tile-action>
        </v-list-tile>
        <v-list-tile avatar v-for="(item, i) in value" :key="i">
          <v-hover>
            <v-list-tile-content slot-scope="{ hover }">
              <v-container fuild>
                <v-layout row>
                  <v-flex shrink>
                    <v-list-tile-avatar>
                      <vue-letter-avatar
                        :name="item.name"
                        size="40"
                        :rounded="true"
                      ></vue-letter-avatar>
                    </v-list-tile-avatar>
                  </v-flex>
                  <v-flex grow>
                    <v-list-tile-title> {{ item.name }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ item.description }}
                    </v-list-tile-sub-title>
                  </v-flex>
                  <v-flex shrink>
                    <v-tooltip bottom>
                      <v-btn
                        v-if="hover"
                        slot="activator"
                        icon
                        flat
                        small
                        :to="{
                          name: 'profile-tree-detail',
                          params: { hexId: item.hexId }
                        }"
                      >
                        <v-icon>link</v-icon>
                      </v-btn>
                      <span>Quick Link</span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-list-tile-content>
          </v-hover>
        </v-list-tile>
      </v-list>
      <div
        v-else-if="editActive"
        key="edit-list"
        style="max-height: 760px"
        class="scroll-y"
      >
        <profile-picker
          :mini="true"
          :checkbox="true"
          :checked.sync="selectedIds"
          :items="allProfiles"
        >
        </profile-picker>
      </div>
    </transition>
  </v-card>
</template>
